// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FieldOperation, FieldOperationDevice } from '../../services/fieldService';
import { getActiveOperationsForMapEditor } from '../../services/mapEditorService';
import { OperationTemplate, OperationTemplateRequest, TemplateDevicePosition } from '../../types/mapEditor';
import { getDeviceColor } from './DeviceIcons';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import ConfirmModal from '../common/ConfirmModal';

const PanelContainer = styled.div<{ $isCollapsed: boolean }>`
  width: ${props => props.$isCollapsed ? '40px' : '320px'};
  background: #1a1a2e;
  border-left: 1px solid #333;
  display: flex;
  flex-direction: column;
  transition: width 0.2s ease;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #252538;
  border-bottom: 1px solid #333;
`;

const HeaderTitle = styled.h3<{ $visible: boolean }>`
  margin: 0;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  display: ${props => props.$visible ? 'block' : 'none'};
`;

const CollapseButton = styled.button`
  background: transparent;
  border: none;
  color: #FFFFFF;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #fff;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const PanelContent = styled.div<{ $visible: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.$visible ? '12px' : '0'};
  display: ${props => props.$visible ? 'flex' : 'none'};
  flex-direction: column;
  gap: 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.div`
  color: #FFFFFF;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  background: #0a0a15;
  border: 1px solid #333;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  option {
    background: #0a0a15;
    color: #fff;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: #0a0a15;
  border: 1px solid #333;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  &::placeholder {
    color: #666;
  }
`;

const Button = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  padding: 10px 16px;
  background: ${props => props.$danger ? '#f44336' : props.$primary ? '#4CAF50' : 'transparent'};
  border: 1px solid ${props => props.$danger ? '#f44336' : props.$primary ? '#4CAF50' : '#444'};
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$danger ? '#d32f2f' : props.$primary ? '#45a049' : '#333'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

const DeviceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 300px;
  overflow-y: auto;
`;

const DeviceItem = styled.div<{ $selected: boolean; $placed: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: ${props => props.$selected ? 'rgba(76, 175, 80, 0.2)' : props.$placed ? 'rgba(33, 150, 243, 0.1)' : '#0a0a15'};
  border: 1px solid ${props => props.$selected ? '#4CAF50' : props.$placed ? '#2196F3' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$selected ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255,255,255,0.05)'};
  }
`;

const DeviceIcon = styled.div<{ $color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: bold;
`;

const DeviceInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DeviceName = styled.div`
  color: #FFFFFF;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeviceType = styled.div`
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 600;
`;

const PlacedBadge = styled.span`
  background: #2196F3;
  color: #fff;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
`;


const DeleteTemplateButton = styled.button`
  background: #f44336;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  width: 100%;
  margin-top: 8px;

  &:hover {
    background: #d32f2f;
  }
`;

const EmptyMessage = styled.div`
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  padding: 20px;
`;

const LoadingMessage = styled.div`
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  padding: 20px;
`;

const Hint = styled.div`
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 4px;
  padding: 10px;
  color: #4CAF50;
  font-size: 12px;
`;

const ScaleControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #0a0a15;
  border-radius: 4px;
  margin-top: 4px;
`;

const ScaleLabel = styled.span`
  color: #FFFFFF;
  font-size: 12px;
  font-weight: 500;
  min-width: 50px;
`;

const ScaleSlider = styled.input`
  flex: 1;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: #333;
  border-radius: 2px;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #4CAF50;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #4CAF50;
    border: none;
    cursor: pointer;
  }
`;

const ScaleValue = styled.span`
  color: #fff;
  font-size: 12px;
  min-width: 35px;
  text-align: right;
`;

interface DevicesPanelProps {
  fieldId: string;
  mapId: string;
  templates: OperationTemplate[];
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string | null) => void;
  onSaveTemplate: (template: OperationTemplateRequest) => Promise<void>;
  onDeleteTemplate: (templateId: string) => Promise<void>;
  onDeviceSelect: (device: TemplateDevicePosition | null, index: number | null) => void;
  selectedDeviceIndex: number | null;
  devicePositions: TemplateDevicePosition[];
  onDevicePositionsChange: (positions: TemplateDevicePosition[]) => void;
  onShowSuccess?: (title: string, message: string, duration?: number) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const DevicesPanel: React.FC<DevicesPanelProps> = ({
  fieldId,
  mapId,
  templates,
  selectedTemplateId,
  onSelectTemplate,
  onSaveTemplate,
  onDeleteTemplate,
  onDeviceSelect,
  selectedDeviceIndex,
  devicePositions,
  onDevicePositionsChange,
  onShowSuccess,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  // All configs (each purchase is a separate item)
  const [configs, setConfigs] = useState<FieldOperation[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [loadingConfigs, setLoadingConfigs] = useState(false);

  const [newTemplateName, setNewTemplateName] = useState('');
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingSelectTemplateName, setPendingSelectTemplateName] = useState<string | null>(null);

  // Confirm modal
  const { confirm, modalProps } = useConfirmModal();

  // Get selected config
  const selectedConfig = configs.find(c => c.id === selectedConfigId) || null;

  // Load all active configs on mount
  useEffect(() => {
    const loadConfigs = async () => {
      setLoadingConfigs(true);
      try {
        const ops = await getActiveOperationsForMapEditor();
        setConfigs(ops);
      } catch (err) {
        console.error('[DevicesPanel] Failed to load configs:', err);
      } finally {
        setLoadingConfigs(false);
      }
    };
    loadConfigs();
  }, []);

  // Get devices with their positions
  const getDevicesWithPositions = useCallback(() => {
    if (!selectedConfig?.devices) return [];

    return selectedConfig.devices.map((device, index) => {
      const existingPosition = devicePositions.find(
        (dp) => dp.fmdId === device.fmdId
      );

      return {
        ...device,
        ofcId: selectedConfig.id,
        index,
        position: existingPosition,
      };
    });
  }, [selectedConfig, devicePositions]);

  const devicesWithPositions = getDevicesWithPositions();

  // Handle config change
  const handleConfigChange = (configId: string) => {
    setSelectedConfigId(configId);
    onSelectTemplate(null);
    onDevicePositionsChange([]);
    onDeviceSelect(null, null);
  };

  // Handle device click - select for placement
  const handleDeviceClick = (device: any, index: number) => {
    if (selectedDeviceIndex === index) {
      onDeviceSelect(null, null);
    } else {
      const devicePosition: TemplateDevicePosition = {
        fmdId: device.fmdId,
        ofcId: selectedConfig?.id || '',
        deviceCode: device.deviceCode || '',
        deviceName: device.model,
        deviceType: device.deviceType,
        model: device.model,
        iconUrl: device.iconUrl || '',
        positionX: 0,
        positionY: 0,
        scale: 1.0,
        locked: false,
        displayNumber: index + 1,
      };
      onDeviceSelect(devicePosition, index);
    }
  };

  // Handle device scale change
  const handleScaleChange = (fmdId: string, newScale: number) => {
    const updatedPositions = devicePositions.map(dp =>
      dp.fmdId === fmdId ? { ...dp, scale: newScale } : dp
    );
    onDevicePositionsChange(updatedPositions);
  };

  // Handle creating new template
  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim() || !selectedConfigId) return;

    const templateName = newTemplateName.trim();
    setSaving(true);
    try {
      const template: OperationTemplateRequest = {
        fieldId,
        mapId,
        name: templateName,
        description: '',
        operationFieldConfigsId: selectedConfigId,  // configId - ID конкретной покупки
        devices: [],  // Пустой список - устройства будут добавляться после создания
      };
      await onSaveTemplate(template);
      setPendingSelectTemplateName(templateName);  // Запоминаем имя для авто-выбора
      setNewTemplateName('');
      setIsCreatingTemplate(false);
    } catch (err) {
      console.error('Failed to create template:', err);
    } finally {
      setSaving(false);
    }
  };

  // Auto-select newly created template
  useEffect(() => {
    if (pendingSelectTemplateName && selectedConfigId) {
      const newTemplate = templates.find(
        t => t.operationFieldConfigsId === selectedConfigId && t.name === pendingSelectTemplateName
      );
      if (newTemplate) {
        onSelectTemplate(newTemplate.id);
        setPendingSelectTemplateName(null);
      }
    }
  }, [templates, pendingSelectTemplateName, selectedConfigId]);

  // Handle saving current positions to template
  const handleSavePositions = async () => {
    if (!selectedTemplateId) return;

    const template = templates.find(t => t.id === selectedTemplateId);
    if (!template) return;

    setSaving(true);
    try {
      const updatedTemplate: OperationTemplateRequest = {
        id: selectedTemplateId,  // Pass id to indicate this is an update
        fieldId,
        mapId,
        name: template.name,
        description: template.description,
        operationFieldConfigsId: template.operationFieldConfigsId,
        devices: devicePositions,
      };
      await onSaveTemplate(updatedTemplate);
      onShowSuccess?.('Saved', 'Device positions saved to template', 3000);
    } catch (err) {
      console.error('Failed to save positions:', err);
    } finally {
      setSaving(false);
    }
  };

  // Load template positions when selected
  useEffect(() => {
    if (selectedTemplateId) {
      const template = templates.find(t => t.id === selectedTemplateId);
      if (template) {
        setSelectedConfigId(template.operationFieldConfigsId);  // operationFieldConfigsId = configId
        onDevicePositionsChange(template.devices);
      }
    }
  }, [selectedTemplateId, templates]);

  // Filter templates for selected config
  const configTemplates = templates.filter(t => t.operationFieldConfigsId === selectedConfigId);

  return (
    <PanelContainer $isCollapsed={isCollapsed}>
      <PanelHeader>
        <HeaderTitle $visible={!isCollapsed}>Devices</HeaderTitle>
        <CollapseButton onClick={onToggleCollapse} title={isCollapsed ? 'Expand' : 'Collapse'}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            {isCollapsed ? (
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            ) : (
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            )}
          </svg>
        </CollapseButton>
      </PanelHeader>

      <PanelContent $visible={!isCollapsed}>
        {/* Config Selection */}
        <Section>
          <SectionTitle>Operation (purchase)</SectionTitle>
          {loadingConfigs ? (
            <LoadingMessage>Loading operations...</LoadingMessage>
          ) : configs.length === 0 ? (
            <EmptyMessage>No active operations</EmptyMessage>
          ) : (
            <Select
              value={selectedConfigId}
              onChange={(e) => handleConfigChange(e.target.value)}
            >
              <option value="">-- Select operation --</option>
              {configs.map(cfg => (
                <option key={cfg.id} value={cfg.id}>
                  {cfg.operationName}{cfg.configUniqueCode ? ` (${cfg.configUniqueCode})` : ''} - {cfg.deviceCount} devices
                </option>
              ))}
            </Select>
          )}
        </Section>

        {/* Templates Section */}
        {selectedConfig && (
          <Section>
            <SectionTitle>Placement Template</SectionTitle>
            <Select
              value={selectedTemplateId || ''}
              onChange={(e) => onSelectTemplate(e.target.value || null)}
            >
              <option value="">-- Select template --</option>
              {configTemplates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </Select>

            <ButtonRow>
              {isCreatingTemplate ? (
                <>
                  <Input
                    type="text"
                    placeholder="Template name..."
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    autoFocus
                    style={{ flex: 1 }}
                  />
                  <Button onClick={() => setIsCreatingTemplate(false)}>Cancel</Button>
                  <Button
                    $primary
                    onClick={handleCreateTemplate}
                    disabled={!newTemplateName.trim() || saving}
                  >
                    {saving ? '...' : 'Create'}
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => setIsCreatingTemplate(true)} style={{ flex: 1 }}>
                    + New Template
                  </Button>
                  {selectedTemplateId && (
                    <DeleteTemplateButton
                      onClick={async () => {
                        const template = configTemplates.find(t => t.id === selectedTemplateId);
                        if (!template) return;

                        const confirmed = await confirm({
                          title: 'Delete Template',
                          message: `Are you sure you want to delete template "${template.name}"?`,
                          confirmText: 'Delete',
                          cancelText: 'Cancel',
                          variant: 'danger',
                        });

                        if (confirmed) {
                          onDeleteTemplate(selectedTemplateId);
                        }
                      }}
                      style={{ flex: 0, width: 'auto', marginTop: 0 }}
                    >
                      Delete
                    </DeleteTemplateButton>
                  )}
                </>
              )}
            </ButtonRow>

            {!selectedTemplateId && !isCreatingTemplate && (
              <Hint>
                Select an existing template or create a new one
              </Hint>
            )}
          </Section>
        )}

        {/* Devices List - show only when template is selected */}
        {selectedTemplateId && selectedConfig && (
          <Section>
            <SectionTitle>
              Devices ({selectedConfig?.deviceCount || 0})
            </SectionTitle>

            {!selectedConfig?.devices?.length ? (
              <EmptyMessage>No devices</EmptyMessage>
            ) : (
              <>
                {selectedDeviceIndex !== null && (
                  <Hint>
                    Click on the canvas to place the device
                  </Hint>
                )}

                <DeviceList>
                  {devicesWithPositions.map((device, index) => (
                    <div key={device.fmdId}>
                      <DeviceItem
                        $selected={selectedDeviceIndex === index}
                        $placed={!!device.position}
                        onClick={() => handleDeviceClick(device, index)}
                      >
                        <DeviceIcon $color={getDeviceColor(device.deviceType)}>
                          {index + 1}
                        </DeviceIcon>
                        <DeviceInfo>
                          <DeviceName>{device.deviceType}</DeviceName>
                          <DeviceType>{device.model} | {device.deviceCode}</DeviceType>
                        </DeviceInfo>
                        {device.position && <PlacedBadge>On map</PlacedBadge>}
                      </DeviceItem>
                      {/* Scale control for placed devices */}
                      {device.position && (
                        <ScaleControl onClick={(e) => e.stopPropagation()}>
                          <ScaleLabel>Size:</ScaleLabel>
                          <ScaleSlider
                            type="range"
                            min="0.5"
                            max="2.0"
                            step="0.1"
                            value={device.position.scale}
                            onChange={(e) => handleScaleChange(device.fmdId, parseFloat(e.target.value))}
                          />
                          <ScaleValue>{device.position.scale.toFixed(1)}x</ScaleValue>
                        </ScaleControl>
                      )}
                    </div>
                  ))}
                </DeviceList>
              </>
            )}
          </Section>
        )}

        {/* Save Button */}
        {selectedTemplateId && devicePositions.length > 0 && (
          <Button
            $primary
            onClick={handleSavePositions}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save positions'}
          </Button>
        )}
      </PanelContent>

      <ConfirmModal {...modalProps} />
    </PanelContainer>
  );
};

export default DevicesPanel;
